import gql from "graphql-tag"
import groupBy from "lodash/groupBy"
import uniqBy from "lodash/uniqBy"
import * as React from "react"
import { useContext, useMemo } from "react"
import { useParams } from "react-router"
import { calculateShouldFetchSingleCommunity } from "../../api/records"
import { placesMapFragment } from "../../graphql/fragments"
import { useQuery, useRedirect } from "../../hooks"
import useRecordLookup from "../../hooks/useRecordLookup"
import { Place } from "../../models"
import { CurrentUserContext } from "../../providers/CurrentUserContext"
import Routes from "../../startup/routes"
import { isLoadFailure, isNotFoundError } from "../../util/graphqlErrors"
import { LocalStorage } from "../../util/window"
import { RecordLoadFailed, RecordNotFound } from "../components/misc/RecordNotFound"
import PlacesList from "../components/places/PlacesList"
import { PlaceViewQuery } from "./PlaceViewContainer"

const PlacesListQuery = gql`
  query PlacesList($communitySlug: String!, $shouldFetchSingleCommunity: Boolean = true) {
    community(slug: $communitySlug) @include(if: $shouldFetchSingleCommunity) {
      id
      slug
      geometry: geo
      centerCoordinates: centerCoordinatesLonlat
      placesMap @include(if: $shouldFetchSingleCommunity) {
        ...placesMapFragment
      }
    }
    currentUser @skip(if: $shouldFetchSingleCommunity) {
      id
      subscribedPlacesMap {
        ...placesMapFragment
      }
    }
  }
  ${placesMapFragment}
`

const PlacesListContainer: React.FC = () => {
  const { idOrName, id: placeSlug } = useParams()
  const { currentUser, loading: loadingCurrentUser } = useContext(CurrentUserContext)
  const communitySlug = idOrName || currentUser?.primaryCommunity?.slug || LocalStorage.getItem("customDomainOrigin") || ""

  const shouldFetchSingleCommunity = calculateShouldFetchSingleCommunity(idOrName, currentUser)
  const isUnknownCommunity = shouldFetchSingleCommunity && !communitySlug

  const { data, loading, error: placesError } = useQuery(PlacesListQuery, {
    variables: { communitySlug, shouldFetchSingleCommunity },
    skip: isUnknownCommunity || loadingCurrentUser,
    // A place's page says its nearby places couldn't load itself; the plain list keeps the "Server error" note (CVL-145, CVL-158)
    context: { silentErrors: !!placeSlug },
  })
  const nearbyPlacesFailed = !!placeSlug && isLoadFailure(placesError)

  // A place address with no town opens the place, as an event address does (CVL-145).
  useRedirect(!loadingCurrentUser && isUnknownCommunity && !placeSlug && Routes.unauthenticated())
  let didRedirect = !loadingCurrentUser && isUnknownCommunity && !placeSlug
  const community = data?.community
  useRedirect(
    !didRedirect && shouldFetchSingleCommunity && community && community.slug !== idOrName && !placeSlug && Routes.places(community),
  )
  didRedirect ||= shouldFetchSingleCommunity && community && community.slug !== idOrName && !placeSlug

  const places: Place[] = useMemo(
    () => (shouldFetchSingleCommunity ? data?.community?.placesMap : data?.currentUser?.subscribedPlacesMap) || [],
    [data, shouldFetchSingleCommunity],
  )

  const communities = useMemo(() => uniqBy(places.map(place => place.community).filter(Boolean), "id"), [places])

  const {
    data: currentPlaceData,
    loading: loadingCurrentPlace,
    error: currentPlaceError,
  } = useQuery(PlaceViewQuery, {
    variables: { placeSlug },
    skip: !placeSlug,
    // The page says "Something went wrong" itself (CVL-145)
    context: { silentErrors: true },
  })

  const currentPlaceFromPlaces = places.find(place => place.slug === placeSlug)
  const lookedUpPlace = currentPlaceData?.place
  // Under a town, a place that belongs to no town is not found, as for events and stories (CVL-145).
  const isTownlessUnderTown = !!idOrName && !!lookedUpPlace && !lookedUpPlace.communities?.length
  const currentPlace = isTownlessUnderTown ? null : lookedUpPlace || currentPlaceFromPlaces
  if (currentPlace && !currentPlaceFromPlaces) places.push(currentPlace)
  const primaryCommunity = data?.community || communities[0]
  // A place address that matches nothing says so rather than opening the list, and a failed
  // lookup says something went wrong (CVL-138).
  const placeLookup = useRecordLookup({
    loading: loading || loadingCurrentUser || loadingCurrentPlace,
    error: currentPlaceError,
    record: currentPlace,
  })
  const placeLoadFailed = !!placeSlug && placeLookup === "failed"
  const isPlaceMissing = placeLoadFailed || (!!placeSlug && placeLookup === "notFound")

  const redirectPath = useMemo(() => {
    if (loading || loadingCurrentUser || loadingCurrentPlace || isPlaceMissing) return false

    // A mistyped town opens the place at its own address (CVL-158)
    if (idOrName && currentPlace && isNotFoundError(placesError)) return Routes.place(currentPlace)
    if (idOrName && primaryCommunity && primaryCommunity.slug !== idOrName) {
      return currentPlace ? Routes.place(currentPlace) : Routes.places(primaryCommunity)
    }
  }, [loading, loadingCurrentUser, loadingCurrentPlace, isPlaceMissing, currentPlace, idOrName, primaryCommunity, placesError])
  useRedirect(!didRedirect && redirectPath)
  didRedirect ||= redirectPath

  const [hiddenCategories, setHiddenCategories] = React.useState({})

  const placeCategories = Object.entries(groupBy(places, "category")).map(([name, groupedPlaces]: [string, [Place]]) => ({
    name,
    count: groupedPlaces.length,
    hidden: Boolean(hiddenCategories[name]),
  }))

  const filteredPlaces = React.useMemo(() => places.filter(place => !hiddenCategories[place.category]), [places, hiddenCategories])

  if (didRedirect) return null
  if (placeLoadFailed) return <RecordLoadFailed />
  if (isPlaceMissing) return <RecordNotFound kind="place" backTo={Routes.places(communitySlug)} backLabel="Back to Places" />

  return (
    <PlacesList
      loading={loadingCurrentUser || loading}
      communityIdParam={communitySlug}
      allPlaces={places}
      place={currentPlace}
      places={filteredPlaces}
      placeCategories={placeCategories}
      togglePlaceCategory={(category): void => setHiddenCategories(current => ({ ...current, [category.name]: !current[category.name] }))}
      primaryCommunity={primaryCommunity}
      nearbyPlacesFailed={nearbyPlacesFailed}
    />
  )
}

export default PlacesListContainer
